import React, { useMemo } from 'react';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import Analytics from './Analytics';
import Workload from './Workload';
import ActiveProjectsAnalytics from './ActiveProjectsAnalytics';
import { AnalyticsPageHero, analyticsPanelSx } from './analyticsSurface';

function getRoleMode(authUser, portalMode) {
    const mode = String(portalMode || '').trim().toLowerCase();
    if (mode === 'developer' || mode === 'funder') return mode;
    const role = String(authUser?.role || '').trim().toLowerCase();
    if (role === 'admin' || role === 'surveyor' || role === 'developer' || role === 'funder') return role;
    return 'surveyor';
}

function clampTab(value, canViewWorkload) {
    const v = String(value || '').trim().toLowerCase();
    if (v === 'active_projects') return 'active_projects';
    if (canViewWorkload && v === 'workload') return 'workload';
    return 'portfolio';
}

function getRoleCopy(roleMode) {
    if (roleMode === 'admin') {
        return {
            eyebrow: 'Analytics Workspace',
            title: 'Portfolio and operational analytics',
            chips: ['Admin scope', 'Portfolio-wide view', 'Risk and capacity signals']
        };
    }
    if (roleMode === 'funder') {
        return {
            eyebrow: 'Portfolio Risk View',
            title: 'Portfolio-wide project risk analytics',
            chips: ['Funder scope', 'Portfolio-wide risks', 'No workload controls']
        };
    }
    if (roleMode === 'developer') {
        return {
            eyebrow: 'Project Risk View',
            title: 'Single-project risk analytics',
            chips: ['Developer scope', 'Single-project first', 'Risk drilldown only']
        };
    }
    return {
        eyebrow: 'Delivery Intelligence',
        title: 'Risk and workload analytics',
        chips: ['Surveyor scope', 'All instructions', 'Risk and capacity signals']
    };
}

export default function AnalyticsHub({ tab, onTabChange, authUser, portalMode, onGoToReports }) {
    const roleMode = useMemo(() => getRoleMode(authUser, portalMode), [authUser, portalMode]);
    const canViewWorkload = roleMode === 'admin' || roleMode === 'surveyor';
    const activeTab = clampTab(tab, canViewWorkload);
    const copy = getRoleCopy(roleMode);

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            <AnalyticsPageHero
                eyebrow={copy.eyebrow}
                title={copy.title}
                chips={copy.chips}
            />

            <Paper sx={{ ...analyticsPanelSx, overflow: 'visible' }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, next) => onTabChange && onTabChange(clampTab(next, canViewWorkload))}
                    sx={{
                        px: { xs: 1.5, sm: 2.5 },
                        pt: { xs: 1.2, sm: 1.5 },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: 999
                        }
                    }}
                >
                    <Tab value="portfolio" label={roleMode === 'developer' ? 'Project Risk' : 'Portfolio Analytics'} />
                    {canViewWorkload ? <Tab value="workload" label="Workload" /> : null}
                    <Tab value="active_projects" label="Active Projects" />
                </Tabs>

                <Box sx={{ px: { xs: 1.2, sm: 1.6 }, pb: { xs: 1.4, sm: 1.8 } }}>
                    {activeTab === 'portfolio' ? <Analytics roleMode={roleMode} onGoToReports={onGoToReports} /> : null}
                    {activeTab === 'workload' && canViewWorkload ? <Workload roleMode={roleMode} isAdmin={roleMode === 'admin'} /> : null}
                    {activeTab === 'active_projects' ? <ActiveProjectsAnalytics /> : null}
                </Box>
            </Paper>
        </Box>
    );
}